import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import { axiosInterceptor, apiCall } from 'services'
import { API_BASE_URL } from 'services/constants'

export type AttachmentKind = 'image' | 'video' | 'audio' | 'file'

export interface ChatAttachment {
	url: string
	name: string
	size?: number
	mimeType: string
	kind: AttachmentKind
	width?: number
	height?: number
	libraryImageId?: string
	/** Local-only: object URL used for the pending preview before upload finishes. */
	previewUrl?: string
	/** Local-only: set while the upload is in flight. */
	uploading?: boolean
	/** Local-only: set when the upload failed, so the row can show a retry. */
	error?: string
	/** Local-only: stable key for React lists before a url exists. */
	tempId?: string
}

export function kindForFile(file: File): AttachmentKind {
	if (file.type.startsWith('image/')) return 'image'
	if (file.type.startsWith('video/')) return 'video'
	if (file.type.startsWith('audio/')) return 'audio'
	return 'file'
}

/**
 * Cloudinary's resource type for a kind.
 *
 * **Audio goes up as 'video'.** That is not a workaround — Cloudinary has no
 * audio resource type and models sound as video without a picture. Sending it
 * as 'raw' would store the bytes but give up the transcoding and streaming
 * delivery that make a voice message play on a phone.
 */
export function resourceTypeForKind(kind: AttachmentKind): 'image' | 'video' | 'raw' {
	if (kind === 'image') return 'image'
	if (kind === 'video' || kind === 'audio') return 'video'
	return 'raw'
}

/**
 * Uploads one chat attachment and, where the chat is scoped to a location,
 * files it in that location's image library.
 *
 * Project chats deliberately write to the PARENT LOCATION's library with
 * `projectid` set, rather than to a project library of their own:
 * `projectImageModel.js` exists but has no routes and is not registered in
 * server.js, so there is no project image API to write to. `locationImageModel`
 * already carries a `projectid` field, so the media stays attributable to the
 * project while living somewhere that actually works.
 *
 * The library write is best-effort. If it fails the attachment still sends,
 * because losing the message would be a worse outcome than losing the library
 * entry.
 */
export async function uploadChatAttachment(
	file: File,
	context: { locationId?: string; projectId?: string },
): Promise<ChatAttachment> {
	const kind = kindForFile(file)

	const uploaded = await uploadToCloudinary(file, {
		folder: 'chat',
		resourceType: resourceTypeForKind(kind),
	})

	const attachment: ChatAttachment = {
		url: uploaded.secure_url || uploaded.url,
		name: file.name || uploaded.original_filename || '',
		size: uploaded.bytes ?? file.size,
		mimeType: file.type || '',
		kind,
		width: uploaded.width,
		height: uploaded.height,
	}

	// Only pictures belong in an image library. A voice message filed there
	// would show as a broken thumbnail in the Images tab and the gallery.
	if (context.locationId && kind === 'image') {
		try {
			const api = axiosInterceptor(API_BASE_URL)
			const record: any = await apiCall(
				api.post('/locationimages', {
					locationid: context.locationId,
					projectid: context.projectId,
					name: attachment.name,
					size: attachment.size,
					url: attachment.url,
					uuid: uploaded.public_id,
					source: 'chat',
					type: attachment.mimeType,
					note: context.projectId ? 'Shared in project chat' : 'Shared in location chat',
				}),
			)
			if (record?._id) attachment.libraryImageId = record._id
		} catch (error) {
			// Deliberately swallowed: see the note above about best-effort.
			// eslint-disable-next-line no-console
			console.warn('[chat] attachment uploaded but could not be filed in the image library', error)
		}
	}

	return attachment
}
